<template>
  <Teleport to="body">
    <div v-if="isVisible" class="weaudition-modal-overlay">
      <!-- Backdrop -->
      <div class="weaudition-modal-backdrop" @click="closeModalIfNeeded"></div>

      <!-- Modal Content -->
      <div class="weaudition-modal-container">
        <!-- Header -->
        <div class="weaudition-modal-header">
          <div style="font-weight: 600; font-size: 1.5rem">Reader Request</div>
          <div style="display: flex; align-items: center; gap: 0.5rem">
            <button
              @click="toggleMute"
              class="ui basic icon button"
              style="
                position: relative;
                border: none !important;
                box-shadow: none !important;
                background: transparent !important;
              "
            >
              <i
                class="large volume icon"
                :class="isMuted ? 'off red' : 'up'"
                style="margin: 0"
              ></i>
            </button>
            <button
              @click="closeModal"
              class="ui basic icon button"
              style="
                border: none !important;
                box-shadow: none !important;
                background: transparent !important;
              "
            >
              <i class="large close icon" style="margin: 0"></i>
            </button>
          </div>
        </div>

        <!-- Body -->
        <div class="weaudition-modal-body">
          <div
            v-if="displayedBookings.length === 0"
            style="padding: 1rem; text-align: center; color: #6b7280"
          >
            No active pending requests.
          </div>

          <div v-else id="bookinglist" class="ui two column stackable grid">
            <template
              v-for="(booking, index) in displayedBookings"
              :key="booking.id"
            >
              <div
                class="row"
                :class="booking.showVideoSetup ? 'videosetup' : ''"
                style="
                  display: flex !important;
                  align-items: stretch !important;
                "
              >
                <!-- Left Column: Details or Video Setup -->
                <div
                  class="column"
                  style="display: flex; flex-direction: column"
                >
	                  <template v-if="booking.showVideoSetup">
	                    <div class="reader-media-setup">
	                      <h3 class="ui header">Camera and Microphone</h3>
	                      <div class="preview-box" :class="{ blocked: setupMediaStatus === 'failed' }">
	                        <video
	                          v-show="setupMediaStatus === 'passed'"
	                          :ref="(el) => setVideoRef(el, booking.id)"
	                          muted
	                          playsinline
	                          autoplay
	                        ></video>
	                        <div v-if="setupMediaStatus !== 'passed'" class="preview-placeholder">
	                          <i v-if="setupMediaStatus === 'failed'" class="video slash icon"></i>
	                          <i v-else class="notched circle loading icon"></i>
	                          <strong>{{ setupMediaStatus === "failed" ? "Camera unavailable" : "Camera preview" }}</strong>
	                          <span>{{ setupMediaMessage }}</span>
	                        </div>
	                      </div>

	                      <div class="device-form">
	                        <label>
	                          <span>Camera</span>
	                          <select
	                            v-model="selectedVideoDevice"
	                            :disabled="setupMediaChecking"
	                            @change="switchSetupDevice('video')"
	                          >
	                            <option value="">Default camera</option>
	                            <option
	                              v-for="dev in videoDevices"
	                              :key="dev.deviceId"
	                              :value="dev.deviceId"
	                            >
	                              {{ dev.label || "Camera " + (videoDevices.indexOf(dev) + 1) }}
	                            </option>
	                          </select>
	                        </label>

	                        <label>
	                          <span>Microphone</span>
	                          <select
	                            v-model="selectedAudioDevice"
	                            :disabled="setupMediaChecking"
	                            @change="switchSetupDevice('audio')"
	                          >
	                            <option value="">Default microphone</option>
	                            <option
	                              v-for="dev in audioDevices"
	                              :key="dev.deviceId"
	                              :value="dev.deviceId"
	                            >
	                              {{ dev.label || "Microphone " + (audioDevices.indexOf(dev) + 1) }}
	                            </option>
	                          </select>
	                        </label>
	                      </div>

	                      <div class="meter-wrap">
	                        <div class="meter-label">
	                          <span>Microphone level</span>
	                          <strong>{{ setupMediaStatus === "passed" ? "Listening" : "Not active" }}</strong>
	                        </div>
	                        <div class="meter-track">
	                          <div class="meter-fill" :style="{ width: setupMicrophoneLevel + '%' }"></div>
	                        </div>
	                      </div>

	                      <div v-if="setupMediaStatus === 'failed'" class="ui red message media-error">
	                        {{ setupMediaMessage }}
	                      </div>

	                      <a
	                        :href="
	                          '/bookings/room2025.php?shortcode=' +
	                          booking.shortcode
	                        "
	                        class="ui large green fluid button"
	                        :class="{ disabled: setupMediaStatus !== 'passed' }"
	                        :aria-disabled="setupMediaStatus !== 'passed'"
	                        style="margin-top: 1rem"
	                        @click="handleJoinRoom"
	                      >
	                        <i class="sign in icon"></i> Join Room
	                      </a>
	                    </div>
	                  </template>

                  <template v-else>
                    <!-- Booking Actor Details -->
                    <div
                      class="ui segment"
                      style="padding: 1rem; margin-bottom: 0"
                    >
                      <div class="ui items" style="margin: 0">
                        <div class="item" style="margin: 0">
                          <span class="ui tiny rounded image">
                            <img :src="booking.image" />
                          </span>
                          <div class="content">
                            <div class="header">{{ booking.theirname }}</div>
                            <div
                              class="description"
                              style="
                                margin-top: 4px;
                                font-size: 0.9em;
                                color: #666;
                              "
                            >
                              <div>
                                Total sessions all-time:
                                {{ booking.total_bookings }}
                              </div>
                              <div>
                                Sessions booked with you:
                                {{ booking.mutual_bookings }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Price -->
                    <div style="text-align: center; margin: 1.2rem 0">
                      <div v-if="parseFloat(booking.fee) > 0">
                        <div style="font-size: 1.6em; font-weight: bold">
                          {{ booking.currencysymbol }}{{ booking.fee }} for
                          {{ booking.duration }} minutes
                        </div>
                      </div>
                      <div v-else style="font-size: 1.6em; font-weight: bold">
                        Free for {{ booking.duration }} minutes
                      </div>
                      <div
                        v-if="parseFloat(booking.extrafee) > 0"
                        style="
                          border-top: 1px solid rgba(0, 0, 0, 0.1);
                          margin-top: 8px;
                          padding-top: 6px;
                          text-align: center;
                          font-size: 0.85em;
                          width: 100%;
                          color: #666;
                        "
                      >
                        Extra 15 mins (if requested):
                        <span style="font-weight: bold; color: #333"
                          >{{ booking.currencysymbol }}{{ booking.extrafee }}
                        </span>
                      </div>
                    </div>

                    <!-- Actions or Status Message -->
                    <div
                      v-if="booking.status === 'cancelled'"
                      class="ui message yellow"
                    >
                      This request has been cancelled by the actor
                    </div>
                    <div
                      v-else-if="
                        ['booked', 'declined'].includes(booking.status)
                      "
                      class="ui message blue"
                    >
                      You have
                      {{
                        booking.status === "booked"
                          ? "accepted"
                          : booking.status
                      }}
                      this request in another browser window or on the app.
                    </div>
                    <template v-else>
                      <div class="ui large fluid buttons">
                        <button
                          @click="acceptBooking(booking)"
                          :class="{ loading: booking.isAccepting }"
                          :disabled="booking.isAccepting"
                          class="ui green button"
                          style="width: 48%"
                        >
                          Confirm
                        </button>
                        <a
                          :href="booking.urldecline"
                          @click.prevent="declineBooking($event, booking)"
                          class="ui red button"
                          style="width: 48%"
                          >Decline</a
                        >
                      </div>

                      <!-- Payment Off-platform Logic -->
                      <!--                    <div class="ui segment" style="margin-top: 1rem; padding: 1rem;">-->
                      <!--                        <div style="display: flex; align-items: center; margin-bottom: 0.8rem;">-->
                      <!--                            <img src="https://www.paypalobjects.com/webstatic/icon/pp258.png" style="width: 40px; height: 40px; margin-right: 10px; object-fit: contain;" />-->
                      <!--                            <div>-->
                      <!--                                <strong style="font-size: 1rem;">{{ booking.accept_off_platform_payments ? 'PayPal' : 'PayPal Only' }}</strong><br>-->
                      <!--                                <span style="font-size: 0.85em; color: #666;">(With a WeAudition Assurance) <i class="info circle icon" title="Payments via PayPal are covered by WeAudition's payment assurance"></i></span>-->
                      <!--                            </div>-->
                      <!--                        </div>-->
                      <!--                        <div class="ui toggle checkbox">-->
                      <!--                            <input type="checkbox" :checked="booking.accept_off_platform_payments" @change="e => toggleOffPlatform(e, booking)">-->
                      <!--                            <label>-->
                      <!--                                <span style="font-weight: 600;" :style="{ color: booking.accept_off_platform_payments ? '#21ba45' : '#999' }">Include My Off-Platform Payment Options</span><br>-->
                      <!--                                <span style="font-size: 0.85em;" :style="{ color: booking.accept_off_platform_payments ? '#999' : '#ccc' }">({{ offPlatformMethodsString }})</span><br>-->
                      <!--                                <span style="font-size: 0.8em;" :style="{ color: booking.accept_off_platform_payments ? '#db2828' : '#ccc' }">WeAudition does not offer Payment Assurance</span>-->
                      <!--                            </label>-->
                      <!--                        </div>-->
                      <!--                    </div>-->
                    </template>
                  </template>
                </div>

                <!-- Right Column: Message Thread -->
                <div
                  class="column"
                  style="display: flex; flex-direction: column"
                >
                  <message-thread
                    :messages="booking.messages"
                    :loading="!booking.messagesLoaded"
                    @reply="(text) => replyTo(booking, text)"
                  />
                </div>
                <!-- end right column -->
              </div>
              <!-- end row -->
            </template>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <audio ref="ringAudio" loop preload="auto">
    <source :src="'/assets/audio/ring.ogg'" type="audio/ogg" />
    <source :src="'/assets/audio/ring.mp3'" type="audio/mpeg" />
  </audio>
</template>

<script setup>
import { computed, inject, ref, onMounted, onUnmounted, nextTick } from "vue";
import OT from "../opentok";

const props = defineProps({
  userId: {
    type: [Number, String],
    required: true,
  },
});

const bookingDiagnostics = inject("bookingDiagnostics", null);
const isVisible = ref(false);
const isMuted = ref(false);
const pendingBookings = ref([]);
const ringAudio = ref(null);
const modalStatuses = new Set(["pending", "accepted", "cancelled"]);

// Device Selection State
const videoDevices = ref([]);
const audioDevices = ref([]);
const selectedVideoDevice = ref("");
const selectedAudioDevice = ref("");
const setupMediaStatus = ref("idle");
const setupMediaMessage = ref("Checking camera and microphone access.");
const setupMediaChecking = ref(false);
const setupMicrophoneLevel = ref(0);
let previewStream = null;
let activePreviewBookingId = null;
let setupAudioContext = null;
let setupAnalyser = null;
let setupAnimationFrame = null;
const videoElements = new Map(); // Store refs to video tags

const offPlatformMethodsString = ref("Venmo, CashApp, Zelle");

const bookingPolls = {};
let pageIsLeaving = false;
let audioPlayPromise = null;
let active = false;

const displayedBookings = computed(() =>
  pendingBookings.value.filter((booking) => modalStatuses.has(booking.status))
);

// Register the global method for the beacon poll to invoke
onMounted(() => {
  window.checkPendingBookings = checkPendingBookings;
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("pageshow", onPageShow);
  // Initial check like legacy code did
  checkPendingBookings();
  fetchPaymentMethods();
});

const loadOpenTok = () => {
  return Promise.resolve(OT);
};

onUnmounted(() => {
  pageIsLeaving = true;
  window.removeEventListener("pagehide", onPageHide);
  window.removeEventListener("pageshow", onPageShow);
  cleanupAll();
  if (window.checkPendingBookings === checkPendingBookings) {
    window.checkPendingBookings = null;
  }
});

const toggleMute = () => {
  isMuted.value = !isMuted.value;
  if (isMuted.value) {
    ringAudio.value?.pause();
  } else if (
    isVisible.value &&
    !displayedBookings.value.every((b) => b.showVideoSetup)
  ) {
    playRing();
  }
};

const closeModalIfNeeded = () => {
  // Can click outside to close if not engaged
};

const closeModal = () => {
  isVisible.value = false;
  active = false;
  pauseRing();
};

const syncModalVisibility = () => {
  if (displayedBookings.value.length === 0) {
    closeModal();
    return;
  }

  if (!isVisible.value && !active) {
    isVisible.value = true;
    active = true;
  }

  if (
    !isMuted.value &&
    displayedBookings.value.some((b) => !b.showVideoSetup)
  ) {
    if (ringAudio.value?.paused ?? true) {
      playRing();
    }
  } else {
    pauseRing();
  }
};

const stopBookingPoll = (bookingId) => {
  const poll = bookingPolls[bookingId];
  if (!poll) return;
  poll.stopped = true;
  clearInterval(poll.interval);
  poll.controller?.abort();
  poll.chatController.abort();
  delete bookingPolls[bookingId];
};

const onPageHide = () => {
  pageIsLeaving = true;
  cleanupAll();
};

const onPageShow = (event) => {
  pageIsLeaving = false;
  if (event.persisted) checkPendingBookings();
};

const teardownBooking = (bookingId) => {
  stopBookingPoll(bookingId);

  const booking = pendingBookings.value.find(
    (item) => parseInt(item.id) === parseInt(bookingId)
  );

  if (booking?.otSession) {
    booking.otSession.disconnect();
    delete booking.otSession;
  }

  videoElements.delete(bookingId);

  return booking;
};

const removeBooking = (bookingId) => {
  const booking = teardownBooking(bookingId);
  const shouldRestartPreview = booking?.showVideoSetup;

  pendingBookings.value = pendingBookings.value.filter(
    (item) => parseInt(item.id) !== parseInt(bookingId)
  );

  if (shouldRestartPreview) {
    cleanupVideoPreview();

    const nextVideoSetupBooking = pendingBookings.value.find(
      (item) => item.showVideoSetup
    );
    if (nextVideoSetupBooking) {
      startDevicePreview(nextVideoSetupBooking.id);
    }
  }

  syncModalVisibility();
};

const stopPreviewForBooking = (bookingId) => {
  if (parseInt(activePreviewBookingId) !== parseInt(bookingId)) return;
  cleanupVideoPreview();
};

const mediaErrorMessage = (error) => {
  if (!error) return "Camera and microphone could not be checked.";
  if (error.name === "NotAllowedError") {
    return "Camera or microphone permission is blocked. Allow access in your browser settings.";
  }
  if (error.name === "NotFoundError") {
    return "No camera or microphone was found. Plug one in or choose another device.";
  }
  if (error.name === "NotReadableError") {
    return "Camera or microphone is already in use by another app. Close other video apps and try again.";
  }
  if (error.name === "OverconstrainedError") {
    return "The selected camera or microphone could not be used. Choose another device.";
  }
  return error.message || "Camera and microphone could not be checked.";
};

const fetchPaymentMethods = async () => {
  try {
    const res = await fetch("/api/payment_methods.php");
    const data = await res.json();
    if (data.success && data.data.length > 0) {
      offPlatformMethodsString.value = data.data.map((m) => m.name).join(", ");
    }
  } catch (err) {
    console.error("Failed to fetch payment methods", err);
    bookingDiagnostics?.error(err, {
      source: "ajax",
      event_type: "booking_modal_payment_methods_failed",
      url: "/api/payment_methods.php"
    });
  }
};

const checkPendingBookings = async () => {
  if (pageIsLeaving) return;
  try {
    const res = await fetch(
      "/api/bookings_pending.php?action=query&format=json"
    );
    const text = await res.text();
    const data = text.trim() ? JSON.parse(text) : [];
    if (pageIsLeaving) return;

    // Filter for bookings where we are the reader
    const readerBookings = data.filter(
      (b) => String(b.readerid) === String(props.userId)
    );

    if (readerBookings.length === 0) {
      cleanupAll();
      closeModal();
      return;
    }

    // Process new ones
    readerBookings.forEach((booking) => {
      let existing = pendingBookings.value.find(
        (b) => parseInt(b.id) === parseInt(booking.id)
      );
      if (!existing) {
        // Construct reactive booking object
        const newBooking = {
          ...booking,
          messages: [],
          messagesLoaded: false,
          showVideoSetup: false,
          isAccepting: false,
          id: parseInt(booking.id),
        };
        pendingBookings.value.push(newBooking);
        existing = pendingBookings.value[pendingBookings.value.length - 1];

        // Start polling messages for this specific booking
        startBookingPoll(existing);
      } else {
        existing.status = booking.status;
      }
    });

    // Remove any bookings that disappeared from API
    const readerBookingIds = new Set(
      readerBookings.map((booking) => parseInt(booking.id))
    );
    pendingBookings.value
      .filter((booking) => !readerBookingIds.has(parseInt(booking.id)))
      .forEach((booking) => removeBooking(booking.id));

    syncModalVisibility();
  } catch (err) {
    console.error("Failed to check pending bookings", err);
    bookingDiagnostics?.error(err, {
      source: "ajax",
      event_type: "booking_modal_pending_check_failed",
      url: "/api/bookings_pending.php?action=query&format=json"
    });
  }
};

const playRing = () => {
  if (!ringAudio.value || isMuted.value) return;
  ringAudio.value.currentTime = 0;
  audioPlayPromise = ringAudio.value.play();
  audioPlayPromise.catch((e) => console.warn("Autoplay prevented", e));
};

const pauseRing = () => {
  if (!ringAudio.value) return;
  if (audioPlayPromise !== null) {
    audioPlayPromise
      .then((_) => {
        ringAudio.value?.pause();
      })
      .catch((e) => {});
  } else {
    ringAudio.value.pause();
  }
};

const startBookingPoll = (booking) => {
  if (pageIsLeaving || bookingPolls[booking.id]) return;
  const poll = {
    stopped: false,
    inFlight: false,
    failures: 0,
    controller: null,
    chatController: new AbortController(),
    interval: null
  };
  bookingPolls[booking.id] = poll;

  // Make an initial fetch for the full thread and instantiate OpenTok
  const initChatSession = async () => {
    try {
      const res = await fetch(
        `/api/bookings_pending.php?booking_id=${booking.id}&other_user_id=${booking.partnerid}`,
        { signal: poll.chatController.signal }
      );
      const data = await res.json();

      if (poll.stopped || !data.success) return;

      booking.messages = (data.messages || []).slice().reverse();
      booking.messagesLoaded = true;

      // Connect to OpenTok context
      if (
        data.booking &&
        booking.opentok_session_id &&
        booking.opentok_token &&
        booking.opentok_api_key
      ) {
        await loadOpenTok();
        if (poll.stopped) return;
        try {
          const session = OT.initSession(
            booking.opentok_api_key,
            booking.opentok_session_id
          );
          booking.otSession = session;

          session.on("signal:chat", function (event) {
            // Ignore messages dispatched internally by self
            if (
              session.connection &&
              event.from.connectionId === session.connection.connectionId
            )
              return;

            let text = event.data;
            if (typeof text === "string" && text.trim().startsWith("{")) {
              try {
                const parsed = JSON.parse(text);
                text = parsed?.kind?.text || parsed?.text || parsed?.message || text;
              } catch (e) {}
            }

            booking.messages.push({
              is_you: false,
              message: text,
              user: {
                profile_image: booking.image,
                fullname: booking.theirname,
              },
            });

            // Audible hint
            if (!isVisible.value) playRing();
          });

          session.connect(booking.opentok_token, (error) => {
            if (error) {
              console.error("OT Modal connect error", error);
              bookingDiagnostics?.opentok("booking_modal_ot_connect_error", error, {
                booking_id: booking.id,
                partner_id: booking.partnerid,
                opentok_session_id: booking.opentok_session_id
              });
            }
          });
        } catch (err) {
          console.error("OT Init error", err);
          bookingDiagnostics?.opentok("booking_modal_ot_init_error", err, {
            booking_id: booking.id,
            partner_id: booking.partnerid,
            opentok_session_id: booking.opentok_session_id
          });
        }
      }
    } catch (e) {
      if (poll.stopped || pageIsLeaving) return;
      console.error(e);
      bookingDiagnostics?.error(e, {
        source: "ajax",
        event_type: "booking_modal_thread_load_failed",
        booking_id: booking.id,
        partner_id: booking.partnerid,
        url: `/api/bookings_pending.php?booking_id=${booking.id}&other_user_id=${booking.partnerid}`
      });
    }
  };

  initChatSession();

  // Strictly check status changes (cancels, accepts by other devices) every 2.5s gracefully
  const pollFn = async () => {
    if (poll.stopped || poll.inFlight || pageIsLeaving || navigator.onLine === false) return;
    poll.inFlight = true;
    const controller = new AbortController();
    poll.controller = controller;
    let response = null;
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, 10000);
    try {
      response = await fetch(
        `/api/bookings_pending.php?booking_id=${booking.id}`,
        { signal: controller.signal }
      );
      if (!response.ok) throw new Error(`Booking status request failed with HTTP ${response.status}`);
      const data = await response.json();
      if (poll.stopped || pageIsLeaving) return;
      poll.failures = 0;

      if (!data.success) {
        removeBooking(booking.id);
        return;
      }

      if (data.booking) {
        booking.status = data.booking.status;
        if (data.booking.status === "accepted") {
          pauseRing();
          if (!booking.showVideoSetup) {
            startVideoSetup(booking);
          }
          syncModalVisibility();
          return;
        }

        if (data.booking.status === "cancelled") {
          booking.showVideoSetup = false;
          stopPreviewForBooking(booking.id);
          pauseRing();
          teardownBooking(booking.id);
          syncModalVisibility();
          return;
        }

        if (["booked", "declined"].includes(data.booking.status)) {
          removeBooking(booking.id);
        }
      }
    } catch (e) {
      if (poll.stopped || pageIsLeaving) return;
      poll.failures += 1;
      const transient = timedOut || (!response && e?.name === "TypeError");
      bookingDiagnostics?.error(e, {
        source: "ajax",
        level: transient && poll.failures < 3 ? "warning" : "error",
        event_type: "booking_modal_status_poll_failed",
        meta: {
          online: navigator.onLine,
          timed_out: timedOut,
          consecutive_failures: poll.failures,
          status: response?.status || null
        },
        booking_id: booking.id,
        partner_id: booking.partnerid,
        url: `/api/bookings_pending.php?booking_id=${booking.id}`
      });
    } finally {
      clearTimeout(timeout);
      poll.inFlight = false;
      poll.controller = null;
    }
  };

  poll.interval = setInterval(pollFn, 2500);
  pollFn();
};

const toggleOffPlatform = async (e, booking) => {
  const isChecked = e.target.checked;
  booking.accept_off_platform_payments = isChecked ? 1 : 0;

  try {
    const fd = new FormData();
    fd.append("booking_id", booking.id);
    fd.append("accept_off_platform_payments", isChecked ? 1 : 0);

    await fetch("/controllers/booking/process_payment_toggle.php", {
      method: "POST",
      body: fd,
    });
  } catch (err) {
    console.error("Failed payment toggle", err);
    bookingDiagnostics?.error(err, {
      source: "ajax",
      event_type: "booking_modal_payment_toggle_failed",
      booking_id: booking.id,
      partner_id: booking.partnerid,
      url: "/controllers/booking/process_payment_toggle.php",
      meta: {
        accept_off_platform_payments: isChecked ? 1 : 0
      }
    });
  }
};

const acceptBooking = async (booking) => {
  booking.isAccepting = true;
  pauseRing();

  try {
    bookingDiagnostics?.event("booking_modal_accept_started", "Reader accepted pending booking", {
      booking_id: booking.id,
      partner_id: booking.partnerid
    });
    const res = await fetch(booking.urlconfirm + "&ajax=1");
    const data = await res.json();
    if (data.success || data.answer === "accepted") {
      bookingDiagnostics?.event("booking_modal_accept_succeeded", "Pending booking accepted", {
        booking_id: booking.id,
        partner_id: booking.partnerid
      });
      startVideoSetup(booking);
    } else {
      bookingDiagnostics?.event("booking_modal_accept_rejected", data.message || "Could not accept booking", {
        booking_id: booking.id,
        partner_id: booking.partnerid,
        response: data
      }, "warning");
      alert("Could not accept booking: " + (data.message || "Unknown error"));
      booking.isAccepting = false;
    }
  } catch (err) {
    bookingDiagnostics?.error(err, {
      source: "ajax",
      event_type: "booking_modal_accept_failed",
      booking_id: booking.id,
      partner_id: booking.partnerid,
      url: booking.urlconfirm
    });
    alert("Network error accepting booking. Please try again.");
    booking.isAccepting = false;
  }
};

const declineBooking = (e, booking) => {
  if (!confirm("Are you sure you want to decline this request?")) return;
  bookingDiagnostics?.event("booking_modal_decline_started", "Reader declined pending booking", {
    booking_id: booking.id,
    partner_id: booking.partnerid
  }, "info");
  window.location.href = booking.urldecline;
};

// Messaging
const replyTo = async (booking, text) => {
  const tId = booking.id; // Usually thread is booking ID
  try {
    const fd = new FormData();
    fd.append("thread", tId);
    fd.append("recipientId", booking.partnerid);
    fd.append("message", text);

    // Async fire and forget API persistence (mirrors RoomMessages)
    fetch("/api/messages/new.php", {
      method: "POST",
      body: fd,
    });

    // Optimistic instant append
    booking.messages.push({
      is_you: true,
      message: text,
    });

    // Zero-latency session dispatch
    if (booking.otSession) {
      const signalPayload = JSON.stringify({
        messageId: "msg_" + Date.now(),
        sentDate: new Date().toISOString(),
        sender: {
          senderId: "user_" + (props.userId || ""),
          displayName: "",
          avatar: "",
        },
        kind: {
          text: text,
        },
      });

      booking.otSession.signal(
        {
          type: "chat",
          data: signalPayload,
        },
        (error) => {
          if (error) {
            console.error("Signal failure", error);
            bookingDiagnostics?.opentok("booking_modal_chat_signal_failed", error, {
              booking_id: booking.id,
              partner_id: booking.partnerid
            });
          }
        }
      );
    }
  } catch (e) {
    console.error(e);
    bookingDiagnostics?.error(e, {
      source: "browser",
      event_type: "booking_modal_reply_failed",
      booking_id: booking.id,
      partner_id: booking.partnerid
    });
  }
};

// Video Setup
const setVideoRef = (el, id) => {
  if (el) {
    videoElements.set(id, el);
  }
};

const startVideoSetup = async (booking) => {
  booking.showVideoSetup = true;
  pauseRing();

  selectedVideoDevice.value =
    localStorage.getItem("com.opentok.hardwaresetup.video") || "";
  selectedAudioDevice.value =
    localStorage.getItem("com.opentok.hardwaresetup.audio") || "";

  await nextTick();

  await getDevices();
  startDevicePreview(booking.id);
};

const getDevices = async () => {
  if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices)
    return;
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    videoDevices.value = devices.filter((d) => d.kind === "videoinput");
    audioDevices.value = devices.filter((d) => d.kind === "audioinput");
  } catch (e) {}
};

const startDevicePreview = async (bookingId) => {
  cleanupVideoPreview();

  if (!navigator.mediaDevices?.getUserMedia) {
    setupMediaStatus.value = "failed";
    setupMediaMessage.value = "This browser cannot request camera or microphone access.";
    return;
  }

  const constraints = {
    video: selectedVideoDevice.value
      ? { deviceId: { exact: selectedVideoDevice.value } }
      : true,
    audio: selectedAudioDevice.value
      ? { deviceId: { exact: selectedAudioDevice.value } }
      : true,
  };

  setupMediaChecking.value = true;
  setupMediaStatus.value = "running";
  setupMediaMessage.value = "Checking camera and microphone access.";

  try {
    previewStream = await navigator.mediaDevices.getUserMedia(constraints);
    activePreviewBookingId = bookingId;
    const videoEl = videoElements.get(bookingId);
    if (videoEl) {
      videoEl.srcObject = previewStream;
        videoEl.play().catch(() => {});
    }
    setupMediaStatus.value = "passed";
    setupMediaMessage.value = "Camera and microphone are working.";
    startSetupMeter(previewStream);
    await getDevices();
  } catch (err) {
    setupMediaStatus.value = "failed";
    setupMediaMessage.value = mediaErrorMessage(err);
    console.error("Camera access failed", err);
    bookingDiagnostics?.error(err, {
      source: "browser",
      event_type: "booking_modal_media_check_failed",
      booking_id: bookingId,
      meta: {
        selected_video_device: selectedVideoDevice.value ? "selected" : "default",
        selected_audio_device: selectedAudioDevice.value ? "selected" : "default"
      }
    });
  } finally {
    setupMediaChecking.value = false;
  }
};

const switchSetupDevice = (type) => {
  if (type === "video") {
    localStorage.setItem(
      "com.opentok.hardwaresetup.video",
      selectedVideoDevice.value
    );
  } else {
    localStorage.setItem(
      "com.opentok.hardwaresetup.audio",
      selectedAudioDevice.value
    );
  }
  const b = pendingBookings.value.find((bk) => bk.showVideoSetup);
  if (b) startDevicePreview(b.id);
};

const stopSetupMeter = () => {
  if (setupAnimationFrame) {
    cancelAnimationFrame(setupAnimationFrame);
    setupAnimationFrame = null;
  }
  if (setupAudioContext) {
    setupAudioContext.close().catch(() => {});
    setupAudioContext = null;
  }
  setupAnalyser = null;
  setupMicrophoneLevel.value = 0;
};

const startSetupMeter = (stream) => {
  stopSetupMeter();
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return;

  setupAudioContext = new AudioContext();
  const source = setupAudioContext.createMediaStreamSource(stream);
  setupAnalyser = setupAudioContext.createAnalyser();
  setupAnalyser.fftSize = 256;
  source.connect(setupAnalyser);

  const data = new Uint8Array(setupAnalyser.frequencyBinCount);
  const tick = () => {
    setupAnalyser.getByteFrequencyData(data);
    const average = data.reduce((sum, value) => sum + value, 0) / data.length;
    setupMicrophoneLevel.value = Math.min(100, Math.round((average / 128) * 100));
    setupAnimationFrame = requestAnimationFrame(tick);
  };
  tick();
};

const handleJoinRoom = (event) => {
  if (setupMediaStatus.value !== "passed") {
    event.preventDefault();
    setupMediaMessage.value = "Please allow camera and microphone access before joining the room.";
    return;
  }
  cleanupVideoPreview();
};

const cleanupVideoPreview = () => {
  stopSetupMeter();
  if (activePreviewBookingId !== null) {
    const activeVideoEl = videoElements.get(activePreviewBookingId);
    if (activeVideoEl) {
      activeVideoEl.srcObject = null;
    }
  }
  if (previewStream) {
    previewStream.getTracks().forEach((t) => t.stop());
    previewStream = null;
  }
  setupMediaStatus.value = "idle";
  setupMediaMessage.value = "Checking camera and microphone access.";
  activePreviewBookingId = null;
};

const cleanupAll = () => {
  Object.keys(bookingPolls).forEach(stopBookingPoll);
  pendingBookings.value.forEach((b) => {
    if (b.otSession) {
      b.otSession.disconnect();
      delete b.otSession;
    }
  });
  pendingBookings.value = [];
  videoElements.clear();
  cleanupVideoPreview();
  pauseRing();
};
</script>

<style scoped>
.weaudition-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.weaudition-modal-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
}

.weaudition-modal-container {
  position: relative;
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 100%;
  max-width: 56rem;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: modal-pop 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.weaudition-modal-header {
  border-bottom: 1px solid #e5e7eb;
  padding: 1rem 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #fafafa;
  border-radius: 0.5rem 0.5rem 0 0;
}

.weaudition-modal-body {
  overflow-y: auto;
  padding: 1.25rem;
  flex: 1;
}

.reader-media-setup {
  border: 1px solid #dfe4ea;
  border-radius: 6px;
  flex: 1;
  padding: 16px;
  text-align: left;
}

.reader-media-setup .ui.header {
  margin-top: 0;
}

.preview-box {
  align-items: center;
  aspect-ratio: 16 / 9;
  background: #111;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  overflow: hidden;
}

.preview-box video {
  height: 100%;
  object-fit: cover;
  transform: scaleX(-1);
  width: 100%;
}

.preview-placeholder {
  align-items: center;
  color: #fff;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
  text-align: center;
}

.preview-placeholder .icon {
  color: #db2828;
  font-size: 28px;
  margin: 0;
}

.preview-placeholder .loading.icon {
  color: #fff;
}

.preview-placeholder span {
  color: rgba(255, 255, 255, 0.75);
  font-size: 13px;
  line-height: 1.35;
}

.device-form {
  display: grid;
  gap: 10px;
  margin-top: 12px;
}

.device-form label {
  color: #29333d;
  display: grid;
  font-weight: 700;
  gap: 5px;
}

.device-form select {
  appearance: auto;
  background: #fff;
  border: 1px solid #cfd7df;
  border-radius: 4px;
  color: #1f2933;
  font-size: 14px;
  min-width: 0;
  padding: 8px 10px;
  width: 100%;
}

.meter-wrap {
  margin-top: 12px;
}

.meter-label {
  color: #29333d;
  display: flex;
  justify-content: space-between;
  margin-bottom: 6px;
}

.meter-track {
  background: #d7dde2;
  border-radius: 99px;
  height: 12px;
  overflow: hidden;
}

.meter-fill {
  background: #21ba45;
  height: 100%;
  transition: width 0.08s linear;
}

.media-error {
  margin-top: 12px !important;
}

@keyframes modal-pop {
  0% {
    transform: scale(0.95);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .weaudition-modal-container {
    max-height: 100vh;
    height: 100vh;
    border-radius: 0;
    margin: 0;
  }
  .weaudition-modal-header {
    border-radius: 0;
  }
  .weaudition-modal-overlay {
    padding: 0;
  }
}
</style>
